// ══════════════════════════════════════════════════════════════════════════════════════════════
//  EL BUSCA: A QUIÉN BUSCAS (B-875, 26-sep-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño: «un sistema tipo "Busca" donde sea rápido acceder y salga el avatar de cada usuario (si tiene foto pues la
//  foto, y si no, la inicial) y al pulsar sobre un usuario, su app empiece a vibrar constantemente hasta que se pare [...]
//  pudiendo entablar un chat desde ahi mismo [...] incluso a la hora de mandar el busca, se pueda adjuntar un texto o
//  audio que se transcriba».
//
//  Arriba, lo que quieras decirle (opcional): un texto o una nota de voz. Debajo, el equipo, con su foto o su inicial y un
//  punto verde si tiene la app abierta. Al pulsar a alguien se le manda el busca AL MOMENTO (POST /api/busca): su app
//  vibra y suena hasta que contesta. Mientras, aquí se ve si lo ha recibido y qué contesta («Voy», «Ahora no puedo»), se
//  puede cancelar (un toque sin querer) y se sigue hablando en el chat rápido, que es vuestro chat de equipo.
//
//  B-876 · SOLO EN HORARIO. Quien está fuera de su horario (o de la apertura de la clínica, si no tiene horario propio)
//  sale en gris con el motivo corto; al tocarlo se dice el motivo entero y no se manda nada. Quién puede y quién no lo
//  decide el servidor (routes/busca /equipo, services/buscaEnHorario), que además rechaza el envío si mientras tanto
//  ha cambiado.
//
//  B-983 · Y QUIEN BUSCA, TAMBIÉN EN SU HORARIO. Si quien abre el Busca está fuera de su horario (o de la apertura de la
//  clínica, si no tiene horario propio), arriba se dice por qué y al tocar a alguien no se manda nada. Lo decide el
//  servidor (/equipo, en `yo`), que además rechaza el envío.
//
//  B-894 · POR ROL. La lista sale por rol y no por orden alfabético (de fábrica: doctores, higienistas, auxiliares,
//  recepción, administración, gerencia, el resto, y al final colaboradores, limpieza y almacén; dentro de cada rol, por
//  nombre). El orden lo pone el servidor
//  (routes/busca /equipo, services/buscaOrden) y la administración de la clínica lo cambia aquí mismo con «Ordenar».
import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { msgError } from '../../utils/errores'
import { Avatar, Ico } from '../ui'
import ChatRapido from './ChatRapido'
import { t as i18nT, tx, marca, tDato } from '../../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// t398 · dónde contestó (lo dice el servidor: routes/busca, respondido_en).
export const DONDE = { movil: marca('components.busca.BuscaPanel.en_el_movil', 'en el móvil'), ordenador: marca('components.busca.BuscaPanel.en_el_ordenador', 'en el ordenador'), tableta: marca('components.busca.BuscaPanel.en_la_tableta', 'en la tableta') }
// Lo que dice el servidor o la conexión en directo, en la forma del estado del busca («silenciar» es «silenciado»).
export const respuestaComoEstado = (r) => (r === 'silenciar' ? 'silenciado' : r)

// Lo que se enseña del busca mandado, según cómo va. Pura: la mide la guardia.
// t398 · «Sonando en 2 dispositivos», «Atendido por Bea en el móvil: Voy», «Silenciado por Bea en el ordenador», «Sin
// respuesta» (pasaron sus 3 minutos sin que nadie lo atendiera).
export function estadoDelBusca(activo) {
  if (!activo) return ''
  const n = activo.persona?.nombre || i18nT('components.busca.BuscaPanel.esa_persona', 'Esa persona')
  const donde = DONDE[activo.en] ? ' ' + tDato(DONDE[activo.en]) : ''
  const r = respuestaComoEstado(activo.respuesta)
  if (r === 'voy') return donde ? i18nT('components.busca.BuscaPanel.atendido_por_voy', 'Atendido por {{n}}{{donde}}: Voy', { n, donde }) : i18nT('components.busca.BuscaPanel.n_voy', '{{n}}: Voy', { n })
  if (r === 'ahora_no') return i18nT('components.busca.BuscaPanel.ahora_no_puedo', '{{n}}{{donde}}: Ahora no puedo', { n, donde })
  if (r === 'visto') return i18nT('components.busca.BuscaPanel.lo_ha_visto', '{{n}} lo ha visto{{donde}}', { n, donde })
  if (r === 'silenciado') return i18nT('components.busca.BuscaPanel.silenciado_por', 'Silenciado por {{n}}{{donde}}', { n, donde })
  if (r === 'cancelado') return i18nT('components.busca.BuscaPanel.busca_cancelado', 'Busca cancelado')
  if (r === 'caducado') return i18nT('components.busca.BuscaPanel.sin_respuesta', 'Sin respuesta')
  const d = Number(activo.dispositivos) || 0
  if (d > 0) return i18nT('components.busca.BuscaPanel.plural_sonando_en_dispositivos', { one: 'Sonando en {{count}} dispositivo', other: 'Sonando en {{count}} dispositivos' }, { count: d, _forma: d === 1 ? 'one' : 'other' })
  return activo.conectado ? i18nT('components.busca.BuscaPanel.sonando_en_su_app', 'Sonando en su app…') : i18nT('components.busca.BuscaPanel.enviado_le_llegara_al_movil', 'Enviado: le llegará al móvil. Ahora mismo no tiene la app abierta.')
}

// t398 · lo que llega del servidor (la conexión en directo o GET /busca/:id/estado) sobre el busca mandado, encima de lo que
// había. Pura: la mide la guardia.
export function alDiaDelBusca(activo, d) {
  if (!activo || !d || d.id !== activo.id) return activo
  const out = { ...activo }
  if (d.estado && d.estado !== 'sonando') out.respuesta = respuestaComoEstado(d.estado)
  if (d.respuesta) out.respuesta = respuestaComoEstado(d.respuesta)
  if (d.dispositivos != null) out.dispositivos = Number(d.dispositivos) || 0
  if (d.respondido_en || d.en) out.en = d.respondido_en || d.en
  return out
}

// B-876 · el motivo en corto para la casilla («Fuera de su horario», «Hoy no está»): lo de antes de los dos puntos.
export function motivoCorto(texto) {
  const t = String(texto || '').trim()
  if (!t) return ''
  return t.split(':')[0].replace(/\.$/, '').trim()
}

// B-894 · subir o bajar un rol en la lista de «Ordenar». Pura: la mide la guardia.
export function moverRol(roles, i, paso) {
  const lista = Array.isArray(roles) ? roles.slice() : []
  const j = i + paso
  if (i < 0 || i >= lista.length || j < 0 || j >= lista.length) return lista
  const [r] = lista.splice(i, 1)
  lista.splice(j, 0, r)
  return lista
}

export default function BuscaPanel({ onCerrar }) {
  const { user } = useAuth()
  const toast = useToast()
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['busca-equipo'],
    queryFn: () => api.get('/busca/equipo').then(r => r.data),
    staleTime: 20000,
  })
  const equipo = Array.isArray(data?.equipo) ? data.equipo : []
  // B-983 · quien busca, fuera de su horario: el motivo (o null si puede buscar).
  const yoFuera = data?.yo && data.yo.en_horario === false ? (data.yo.fuera_de_horario || i18nT('components.busca.BuscaPanel.ahora_no_estas_en_tu', 'Ahora no estás en tu horario: el Busca solo se usa dentro de tu horario laboral.')) : null
  // B-894 · «Ordenar»: null = cerrado; si no, los roles en el orden que se está poniendo.
  const [ordenando, setOrdenando] = useState(null)
  const [guardandoOrden, setGuardandoOrden] = useState(false)
  const [texto, setTexto] = useState('')
  const [audio, setAudio] = useState(null)          // { blob, segundos }
  const [grabando, setGrabando] = useState(false)
  const [enviando, setEnviando] = useState(null)    // id de la persona a la que se está mandando
  const [activo, setActivo] = useState(null)        // { persona, id, canalId, conectado, respuesta }
  const grabRef = useRef(null)

  // Lo que contesta llega por la conexión en directo (context/LlamadasContext lo reparte como 'biango-busca'). t398 · y
  // cómo va: en cuántos dispositivos suena, dónde lo contestó, o que se quedó sin respuesta.
  useEffect(() => {
    const on = (e) => {
      const d = (e && e.detail) || {}
      if (d.tipo === 'respondido' || d.tipo === 'estado') setActivo(a => alDiaDelBusca(a, d))
    }
    window.addEventListener('biango-busca', on)
    return () => window.removeEventListener('biango-busca', on)
  }, [])
  // t398 · mientras suena, cada 5 s se pregunta al servidor cómo va (por si se perdió el aviso en directo).
  const activoId = activo && !activo.respuesta ? activo.id : null
  useEffect(() => {
    if (!activoId) return undefined
    const t = setInterval(() => {
      api.get('/busca/' + activoId + '/estado').then(r => setActivo(a => alDiaDelBusca(a, r.data))).catch(() => {})
    }, 5000)
    return () => clearInterval(t)
  }, [activoId])

  const grabar = async () => {
    if (grabando) { try { grabRef.current?.stop() } catch { } return }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const rec = new MediaRecorder(stream)
      const trozos = []
      const inicio = Date.now()
      rec.ondataavailable = (ev) => { if (ev.data && ev.data.size) trozos.push(ev.data) }
      rec.onstop = () => {
        stream.getTracks().forEach(t => t.stop())
        setGrabando(false)
        const blob = new Blob(trozos, { type: rec.mimeType || 'audio/webm' })
        if (blob.size) setAudio({ blob, segundos: Math.max(1, Math.round((Date.now() - inicio) / 1000)) })
      }
      grabRef.current = rec
      rec.start()
      setGrabando(true)
    } catch (e) { toast.error(i18nT('components.busca.BuscaPanel.no_se_puede_usar_el', 'No se puede usar el micrófono: {{x}}', { x: e?.message || i18nT('components.busca.BuscaPanel.sin_permiso', 'sin permiso') })) }
  }

  const buscar = async (p) => {
    if (enviando) return
    // B-983 · fuera de MI horario no se manda: se dice por qué.
    if (yoFuera) { toast.info(yoFuera); return }
    // B-876 · fuera de su horario no se manda: se dice por qué.
    if (p.en_horario === false) { toast.info(p.fuera_de_horario || i18nT('components.busca.BuscaPanel.ahora_no_se_le_puede', 'Ahora no se le puede buscar.')); return }
    setEnviando(p.id)
    try {
      let audioId = null
      if (audio && audio.blob) {
        // La nota de voz va al chat de los dos (que la transcribe) y el busca la lleva consigo.
        const dm = await api.post('/chat/dm/' + p.id)
        const fd = new FormData()
        const tipo = audio.blob.type || 'audio/webm'
        fd.append('archivo', new File([audio.blob], 'nota-de-voz.' + (tipo.includes('mp4') || tipo.includes('m4a') ? 'm4a' : 'webm'), { type: tipo }))
        const sub = await api.post('/chat/canales/' + dm.data.id + '/adjunto', fd)
        audioId = sub.data?.id || null
      }
      const r = await api.post('/busca', { para: p.id, ...(texto.trim() ? { texto: texto.trim() } : {}), ...(audioId ? { audio_mensaje_id: audioId } : {}) })
      setActivo({ persona: p, id: r.data?.id, canalId: r.data?.canal_id, conectado: !!r.data?.conectado, respuesta: null, dispositivos: Number(r.data?.estado?.dispositivos) || 0 })
      setTexto(''); setAudio(null)
    } catch (e) { toast.error(msgError(e, i18nT('components.busca.BuscaPanel.no_se_pudo_mandar_el', 'No se pudo mandar el busca'))) }
    setEnviando(null)
  }
  const cancelar = async () => {
    if (!activo?.id) return
    try { await api.post('/busca/' + activo.id + '/cancelar'); setActivo(a => (a ? { ...a, respuesta: 'cancelado' } : a)) }
    catch (e) { toast.error(msgError(e, i18nT('components.busca.BuscaPanel.no_se_pudo_cancelar', 'No se pudo cancelar'))) }
  }

  const abrirOrden = async () => {
    try { const r = await api.get('/busca/orden'); setOrdenando(Array.isArray(r.data?.roles) ? r.data.roles : []) }
    catch (e) { toast.error(msgError(e, i18nT('components.busca.BuscaPanel.no_se_pudo_cargar_el', 'No se pudo cargar el orden'))) }
  }
  const guardarOrden = async (deFabrica) => {
    if (guardandoOrden) return
    setGuardandoOrden(true)
    try {
      await api.put('/busca/orden', { roles: deFabrica ? null : (ordenando || []).map(r => r.clave) })
      setOrdenando(null)
      toast.success(deFabrica ? i18nT('components.busca.BuscaPanel.vuelve_el_orden_de_fabrica', 'Vuelve el orden de fábrica') : i18nT('components.busca.BuscaPanel.orden_guardado', 'Orden guardado'))
      if (typeof refetch === 'function') refetch()
    } catch (e) { toast.error(msgError(e, i18nT('components.busca.BuscaPanel.no_se_pudo_guardar_el', 'No se pudo guardar el orden'))) }
    setGuardandoOrden(false)
  }

  const caja = { background: 'var(--surface)', borderRadius: 16, border: '1px solid var(--border)', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }
  return (
    <div role="dialog" aria-label={i18nT('components.busca.BuscaPanel.busca', 'Busca')} data-busca-panel onClick={onCerrar}
      style={{ position: 'fixed', inset: 0, zIndex: 2300, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...caja, width: '100%', maxWidth: 560, maxHeight: '100%', overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <b style={{ flex: 1, fontSize: 17 }}>{i18nT('components.busca.BuscaPanel.busca', 'Busca')}</b>
          {data?.puede_ordenar && !activo && ordenando === null && (
            <button type="button" id="busca-ordenar" onClick={abrirOrden}
              style={{ padding: '6px 10px', borderRadius: 10, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
              {i18nT('components.busca.BuscaPanel.ordenar', 'Ordenar')}
            </button>
          )}
          <button type="button" aria-label={i18nT('components.busca.BuscaPanel.cerrar', 'Cerrar')} onClick={onCerrar} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex' }}><Ico n="close" s={20} /></button>
        </div>

        {ordenando !== null && !activo && (
          <div data-busca-ordenar style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <b style={{ fontSize: 14.5 }}>{i18nT('components.busca.BuscaPanel.en_que_orden_sale_el', 'En qué orden sale el equipo, por rol')}</b>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{i18nT('components.busca.BuscaPanel.de_fabrica_doctores_higienistas_auxiliar', 'De fábrica: doctores, higienistas, auxiliares, recepción, administración y gerencia; después el resto, y al final colaboradores, limpieza y almacén. Dentro de cada rol, por nombre. Vale para toda la clínica.')}</span>
            {ordenando.map((r, i) => (
              <div key={r.clave} data-rol-orden={r.clave} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 10, border: '1px solid var(--border)', opacity: r.personas ? 1 : 0.6 }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{r.nombre}</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{i18nT('components.busca.BuscaPanel.plural_personas', { one: '{{count}} persona', other: '{{count}} personas' }, { count: (r.personas || 0), _forma: r.personas === 1 ? 'one' : 'other' })}</span>
                <button type="button" aria-label={i18nT('components.busca.BuscaPanel.subir', 'Subir {{nombre}}', { nombre: r.nombre })} data-subir={r.clave} disabled={i === 0} onClick={() => setOrdenando(o => moverRol(o, i, -1))}
                  style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? 0.4 : 1, fontSize: 15 }}>↑</button>
                <button type="button" aria-label={i18nT('components.busca.BuscaPanel.bajar', 'Bajar {{nombre}}', { nombre: r.nombre })} data-bajar={r.clave} disabled={i === ordenando.length - 1} onClick={() => setOrdenando(o => moverRol(o, i, 1))}
                  style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', cursor: i === ordenando.length - 1 ? 'default' : 'pointer', opacity: i === ordenando.length - 1 ? 0.4 : 1, fontSize: 15 }}>↓</button>
              </div>
            ))}
            {!ordenando.length && <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{i18nT('components.busca.BuscaPanel.no_hay_roles_que_ordenar', 'No hay roles que ordenar.')}</span>}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
              <button type="button" id="busca-orden-guardar" disabled={guardandoOrden || !ordenando.length} onClick={() => guardarOrden(false)}
                style={{ padding: '8px 14px', borderRadius: 10, border: 'none', background: 'var(--primary)', color: 'var(--sobre-primary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{i18nT('components.busca.BuscaPanel.guardar', 'Guardar')}</button>
              <button type="button" id="busca-orden-fabrica" disabled={guardandoOrden} onClick={() => guardarOrden(true)}
                style={{ padding: '8px 12px', borderRadius: 10, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{i18nT('components.busca.BuscaPanel.volver_al_de_fabrica', 'Volver al de fábrica')}</button>
              <button type="button" onClick={() => setOrdenando(null)}
                style={{ padding: '8px 12px', borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{i18nT('components.busca.BuscaPanel.cancelar', 'Cancelar')}</button>
            </div>
          </div>
        )}

        {!activo && ordenando === null && (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input id="busca-texto" value={texto} onChange={e => setTexto(e.target.value)} maxLength={500} placeholder={i18nT('components.busca.BuscaPanel.que_necesitas_opcional', 'Qué necesitas (opcional)')}
                style={{ flex: '1 1 220px', minWidth: 0, padding: '9px 11px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)', fontSize: 14 }} />
              <button type="button" id="busca-grabar" onClick={grabar}
                style={{ padding: '9px 12px', borderRadius: 10, border: '1px solid var(--border)', background: grabando ? '#ef4444' : 'var(--surface)', color: grabando ? '#fff' : 'var(--text-primary)', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Ico n="micro" s={16} /> {grabando ? i18nT('components.busca.BuscaPanel.parar', 'Parar') : i18nT('components.busca.BuscaPanel.nota_de_voz', 'Nota de voz')}
              </button>
            </div>
            {audio && (
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', display: 'flex', gap: 8, alignItems: 'center' }}>
                {tx('components.busca.BuscaPanel.nota_de_voz_lista_s', 'Nota de voz lista ({{segundos}} s): se transcribe al mandarla.', { segundos: audio.segundos })}
                <button type="button" onClick={() => setAudio(null)} style={{ border: 'none', background: 'transparent', color: '#ef4444', fontWeight: 700, cursor: 'pointer' }}>{i18nT('components.busca.BuscaPanel.quitar', 'Quitar')}</button>
              </div>
            )}
            {yoFuera && (
              <div data-busca-yo-fuera role="status" style={{ padding: '9px 11px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, lineHeight: 1.35 }}>
                {yoFuera}
              </div>
            )}
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{i18nT('components.busca.BuscaPanel.pulsa_a_quien_buscas_su', 'Pulsa a quien buscas: su app vibrará y sonará hasta que conteste. Quien está fuera de su horario sale en gris.')}</span>
            {isLoading ? <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{i18nT('components.busca.BuscaPanel.cargando_el_equipo', 'Cargando el equipo…')}</span> : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(92px, 1fr))', gap: 10 }}>
                {equipo.map(p => (
                  <button key={p.id} type="button" data-busca-persona={p.id} data-fuera-de-horario={p.en_horario === false ? '1' : undefined}
                    onClick={() => buscar(p)} disabled={!!enviando} title={p.en_horario === false ? (p.fuera_de_horario || undefined) : undefined}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '10px 6px', borderRadius: 12, border: '1px solid var(--border)', background: enviando === p.id ? 'color-mix(in srgb, var(--primary) 12%, var(--surface))' : 'var(--surface)', cursor: 'pointer', minWidth: 0, opacity: p.en_horario === false || yoFuera ? 0.55 : 1 }}>
                    <span style={{ position: 'relative', display: 'flex' }}>
                      <Avatar src={p.avatar} nombre={p.nombre} size={56} />
                      {p.conectado && <span title={i18nT('components.busca.BuscaPanel.tiene_la_app_abierta', 'Tiene la app abierta')} style={{ position: 'absolute', right: 1, bottom: 1, width: 12, height: 12, borderRadius: '50%', background: '#12a150', border: '2px solid var(--surface)' }} />}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', textAlign: 'center', lineHeight: 1.2, overflowWrap: 'anywhere' }}>{p.nombre}</span>
                    {p.en_horario === false && (
                      <span data-motivo-fuera style={{ fontSize: 10.5, color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.2 }}>{motivoCorto(p.fuera_de_horario) || i18nT('components.busca.BuscaPanel.fuera_de_horario', 'Fuera de horario')}</span>
                    )}
                  </button>
                ))}
                {!equipo.length && <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{i18nT('components.busca.BuscaPanel.no_hay_nadie_mas_en', 'No hay nadie más en el equipo.')}</span>}
              </div>
            )}
          </>
        )}

        {activo && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Avatar src={activo.persona.avatar} nombre={activo.persona.nombre} size={44} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <b style={{ display: 'block', fontSize: 14.5 }}>{activo.persona.nombre}</b>
                <span data-estado-busca style={{ fontSize: 12.5, color: activo.respuesta === 'voy' ? '#12a150' : activo.respuesta === 'caducado' ? '#ef4444' : activo.respuesta ? 'var(--text-secondary)' : 'var(--primary)', fontWeight: 700 }}>{estadoDelBusca(activo)}</span>
              </span>
              {!activo.respuesta && <button type="button" id="busca-cancelar" onClick={cancelar} style={{ padding: '7px 10px', borderRadius: 10, border: '1px solid var(--border)', background: 'transparent', color: '#ef4444', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>{i18nT('components.busca.BuscaPanel.cancelar', 'Cancelar')}</button>}
            </div>
            <ChatRapido canalId={activo.canalId} yoId={user?.id} />
            <button type="button" onClick={() => setActivo(null)} style={{ alignSelf: 'flex-start', padding: '7px 10px', borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--primary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{i18nT('components.busca.BuscaPanel.buscar_a_otra_persona', 'Buscar a otra persona')}</button>
          </>
        )}
      </div>
    </div>
  )
}
